import type { ISerialize } from '@/objects/ISerialize';
import type { TimeExitStatus } from './../enums/TimeExitStatus';
import { formatDateIso } from '@/utilities/dateutils';

export class ResultItem implements ISerialize {
    public AccumulatedTime: number;

    public Bib: number | null;

    public CategoryID: number;

    public ContestantID: number;

    public DiffCategory: number;

    public DiffGender: number;

    public DiffOverall: number;

    public EventID: number;

    public LegNumber: number;

    public PaceOverall: number;

    public PaceSplit: number;

    public PassingTime: Date;

    public RaceContestantID: number;

    public RaceID: number | null;

    public RankCategory: number;

    public RankGender: number;

    public RankOverall: number;

    public StartTime: Date | null;

    public StationRoleID: number;

    public TimeStatus: TimeExitStatus;

    public TrendCategory: number;

    public TrendGender: number;

    public TrendOverall: number;

    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        AccumulatedTime?: number;
        Bib?: number | null;
        CategoryID?: number;
        ContestantID?: number;
        DiffCategory?: number;
        DiffGender?: number;
        DiffOverall?: number;
        EventID?: number;
        LegNumber?: number;
        PaceOverall?: number;
        PaceSplit?: number;
        PassingTime?: Date;
        RaceContestantID?: number;
        RaceID?: number | null;
        RankCategory?: number;
        RankGender?: number;
        RankOverall?: number;
        StartTime?: Date | null;
        StationRoleID?: number;
        TimeStatus?: TimeExitStatus;
        TrendCategory?: number;
        TrendGender?: number;
        TrendOverall?: number;
    }) {
        this.AccumulatedTime = values.AccumulatedTime ?? 0;

        this.Bib = values.Bib ?? null;

        this.CategoryID = values.CategoryID ?? 0;

        this.ContestantID = values.ContestantID ?? 0;

        this.DiffCategory = values.DiffCategory ?? 0;

        this.DiffGender = values.DiffGender ?? 0;

        this.DiffOverall = values.DiffOverall ?? 0;

        this.EventID = values.EventID ?? 0;

        this.LegNumber = values.LegNumber ?? 0;

        this.PaceOverall = values.PaceOverall ?? 0;

        this.PaceSplit = values.PaceSplit ?? 0;

        this.PassingTime = values.PassingTime ?? new Date('1970-01-01T00:00:00.000');

        this.RaceContestantID = values.RaceContestantID ?? 0;

        this.RaceID = values.RaceID ?? null;

        this.RankCategory = values.RankCategory ?? 0;

        this.RankGender = values.RankGender ?? 0;

        this.RankOverall = values.RankOverall ?? 0;

        this.StartTime = values.StartTime ?? null;

        this.StationRoleID = values.StationRoleID ?? 0;

        this.TimeStatus = values.TimeStatus ?? 'None';

        this.TrendCategory = values.TrendCategory ?? 0;

        this.TrendGender = values.TrendGender ?? 0;

        this.TrendOverall = values.TrendOverall ?? 0;
    }

    public serialize(): string {
        return (
            '{' +
            `"AccumulatedTime": ${JSON.stringify(this.AccumulatedTime)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"DiffCategory": ${JSON.stringify(this.DiffCategory)}` +
            ',' +
            `"DiffGender": ${JSON.stringify(this.DiffGender)}` +
            ',' +
            `"DiffOverall": ${JSON.stringify(this.DiffOverall)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"LegNumber": ${JSON.stringify(this.LegNumber)}` +
            ',' +
            `"PaceOverall": ${JSON.stringify(this.PaceOverall)}` +
            ',' +
            `"PaceSplit": ${JSON.stringify(this.PaceSplit)}` +
            ',' +
            `"PassingTime": ${'"' + formatDateIso(this.PassingTime) + '"'}` +
            ',' +
            `"RaceContestantID": ${JSON.stringify(this.RaceContestantID)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"RankCategory": ${JSON.stringify(this.RankCategory)}` +
            ',' +
            `"RankGender": ${JSON.stringify(this.RankGender)}` +
            ',' +
            `"RankOverall": ${JSON.stringify(this.RankOverall)}` +
            ',' +
            `"StartTime": ${this.StartTime ? '"' + formatDateIso(this.StartTime) + '"' : null}` +
            ',' +
            `"StationRoleID": ${JSON.stringify(this.StationRoleID)}` +
            ',' +
            `"TimeStatus": ${this.TimeStatus == 'None' ? 0 : this.TimeStatus == 'Deleted' ? 1 : this.TimeStatus == 'Time' ? 2 : this.TimeStatus == 'Finished' ? 3 : this.TimeStatus == 'REL' ? 4 : this.TimeStatus == 'DNC' ? 5 : this.TimeStatus == 'DNF' ? 6 : this.TimeStatus == 'DSQ' ? 7 : this.TimeStatus == 'DNS' ? 8 : this.TimeStatus == 'DNQ' ? 9 : this.TimeStatus == 'LAP' ? 10 : 0}` +
            ',' +
            `"TrendCategory": ${JSON.stringify(this.TrendCategory)}` +
            ',' +
            `"TrendGender": ${JSON.stringify(this.TrendGender)}` +
            ',' +
            `"TrendOverall": ${JSON.stringify(this.TrendOverall)}` +
            '}'
        );
    }

    /**
     * Creates an instance of ResultItem from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        AccumulatedTime?: number;
        Bib?: number | null;
        CategoryID?: number;
        ContestantID?: number;
        DiffCategory?: number;
        DiffGender?: number;
        DiffOverall?: number;
        EventID?: number;
        LegNumber?: number;
        PaceOverall?: number;
        PaceSplit?: number;
        PassingTime?: Date;
        RaceContestantID?: number;
        RaceID?: number | null;
        RankCategory?: number;
        RankGender?: number;
        RankOverall?: number;
        StartTime?: Date | null;
        StationRoleID?: number;
        TimeStatus?: TimeExitStatus;
        TrendCategory?: number;
        TrendGender?: number;
        TrendOverall?: number;
    }): ResultItem {
        return new ResultItem(values);
    }

    public static _FromServerJson(json: any): ResultItem {
        return new ResultItem({
            AccumulatedTime: json.AccumulatedTime,
            Bib: json.Bib ?? null,
            CategoryID: json.CategoryID,
            ContestantID: json.ContestantID,
            DiffCategory: json.DiffCategory,
            DiffGender: json.DiffGender,
            DiffOverall: json.DiffOverall,
            EventID: json.EventID,
            LegNumber: json.LegNumber,
            PaceOverall: json.PaceOverall,
            PaceSplit: json.PaceSplit,
            PassingTime: json.PassingTime instanceof Date ? json.PassingTime : new Date(json.PassingTime),
            RaceContestantID: json.RaceContestantID,
            RaceID: json.RaceID ?? null,
            RankCategory: json.RankCategory,
            RankGender: json.RankGender,
            RankOverall: json.RankOverall,
            StartTime: json.StartTime ? (json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime)) : null,
            StationRoleID: json.StationRoleID,
            TimeStatus:
                json.TimeStatus == 0
                    ? 'None'
                    : json.TimeStatus == 1
                      ? 'Deleted'
                      : json.TimeStatus == 2
                        ? 'Time'
                        : json.TimeStatus == 3
                          ? 'Finished'
                          : json.TimeStatus == 4
                            ? 'REL'
                            : json.TimeStatus == 5
                              ? 'DNC'
                              : json.TimeStatus == 6
                                ? 'DNF'
                                : json.TimeStatus == 7
                                  ? 'DSQ'
                                  : json.TimeStatus == 8
                                    ? 'DNS'
                                    : json.TimeStatus == 9
                                      ? 'DNQ'
                                      : json.TimeStatus == 10
                                        ? 'LAP'
                                        : 'None',
            TrendCategory: json.TrendCategory,
            TrendGender: json.TrendGender,
            TrendOverall: json.TrendOverall,
        });
    }

    /**
     * Creates an instance of ResultItem from another instance of ResultItem.
     * @param other The ResultItem you want to clone
     * @returns A cloned ResultItem
     */
    public static FromOther(other: ResultItem): ResultItem {
        return ResultItem._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ResultItem.FromOther(this);
    }

    static NAME_OF_CLASS = 'ResultItem';
}
